import { documentsForPlacement } from "../constants/navigationCatalogue";
import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDocumentTypesContext } from "../context/DocumentTypesContext";
import { usePermissions } from "../context/PermissionContext";
import { useTopNavMenu } from "../context/TopNavMenuContext";
import { modulesForDocumentType } from "../constants/documentModule";
import { DocumentType } from "../types/Types";
import { getDocumentPath } from "../routes/documentPath";
import TopNavLink from "./TopNavLink";

type Entry = {
  key: string;
  path: string;
  label: string;
  exact?: boolean;
  state?: Record<string, unknown>;
  /** draws a separator line above this entry (used for the control-panel link) */
  divider?: boolean;
};

const docState = (doc: DocumentType) => ({
  _id: doc._id,
  code: doc.code,
  name: doc.name,
  category: doc.category,
  subCategory: doc.subCategory,
  subType: doc.subType,
});

interface TopStockManagementMenuProps {
  /** 'stock' = Залихи (overview, card, inventory, print); 'warehouse' = Магацин (warehouse documents). Not the same thing. */
  variant?: "stock" | "warehouse";
}

const TopStockManagementMenu: React.FC<TopStockManagementMenuProps> = ({ variant = "stock" }) => {
  const isWarehouseMenu = variant === "warehouse";
  const menuKey = isWarehouseMenu ? "warehouse" : "stock";
  const { t } = useTranslation();
  const location = useLocation();
  const { documentTypes } = useDocumentTypesContext();
  const { hasAccess, hasDocNavigate, hasDocumentAccess, isAdmin } = usePermissions();
  const canOpen = (doc: DocumentType) => modulesForDocumentType(doc).some((m) => hasDocumentAccess(m, doc.code, "read"));
  const [activeSectionKey, setActiveSectionKey] = useState<string | null>(null);
  const { activeMenu, openMenu, closeMenu, closeMenuDelayed, cancelClose } = useTopNavMenu();
  const open = activeMenu === menuKey;
  const menuLabel = isWarehouseMenu ? t("sidebar.warehouse", "Магацин") : t("sidebar.stockManagement", "Залихи");

  const warehouseDocs = useMemo(() => {
    return documentsForPlacement(documentTypes, isWarehouseMenu ? "warehouse" : "stockManagement")
      .sort((a, b) => {
        const numA = parseInt(a.code, 10);
        const numB = parseInt(b.code, 10);
        if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA - numB;
        return a.code.localeCompare(b.code);
      });
  }, [documentTypes, isWarehouseMenu]);

  const inventoryDocs = useMemo(() => {
    return documentsForPlacement(documentTypes, "inventory")
      .sort((a, b) => {
        const numA = parseInt(a.code, 10);
        const numB = parseInt(b.code, 10);
        if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA - numB;
        return a.code.localeCompare(b.code);
      });
  }, [documentTypes]);

  // Залихи needs STOCK_MANAGEMENT. The document entries (both menus) need MATERIAL_DOCUMENTS, the same
  // module the server checks. Магацин shows only if the user has MATERIAL_DOCUMENTS or STOREHOUSE_SEARCH_BOARD.
  if (!isWarehouseMenu && !hasAccess("STOCK_MANAGEMENT")) return null;

  const mainEntries: Entry[] = [
    { key: "warehouse-card", path: "/stock-management/warehouse-card", label: t("sidebar.documentMovements", "Картица на складиште"), exact: true },
    { key: "stock-overview", path: "/stock-management/stock-overview", label: t("sidebar.stockOverview", "Залиха"), exact: true },
  ];

  const documentEntries: Entry[] = [
    ...warehouseDocs
      .filter((doc) => hasDocNavigate(doc.code) && canOpen(doc))
      .map((doc) => ({
        key: doc._id,
        path: getDocumentPath(doc),
        label: `${doc.code} ${doc.name}`.trim(),
        state: docState(doc),
      })),
    ...(isWarehouseMenu && hasAccess("STOREHOUSE_SEARCH_BOARD")
      ? [{
          key: "storehouse-search-board",
          path: "/storehouse/search-board",
          label: t("sidebar.storehouseSearchBoard", "Search Board"),
          divider: true,
        }]
      : []),
  ];

  const inventoryEntries: Entry[] = hasAccess("INVENTORY") || isAdmin
    ? inventoryDocs
        .filter((doc) => hasDocNavigate(doc.code) && canOpen(doc))
        .map((doc) => ({
          key: doc._id,
          path: getDocumentPath(doc),
          label: `${doc.code} ${doc.name}`.trim(),
          state: docState(doc),
        }))
    : [];

  const printEntries: Entry[] = hasAccess("PRINT_CONTROL_PANEL")
    ? [{ key: "stock-print", path: "/stock-control-panel", label: t("sidebar.printControlPanel", "Print"), divider: true }]
    : [];

  // Залихи lists menu group 'warehouse' (01T0, 2026 …); Магацин lists menu group 'storehouse'.
  const sections = (isWarehouseMenu
    ? [
        { key: "documents", label: "Документи", entries: documentEntries },
      ]
    : [
        { key: "main", label: t("sidebar.stockManagement", "Залихи"), entries: mainEntries },
        { key: "documents", label: "Документи", entries: [...documentEntries, ...printEntries] },
        { key: "inventory", label: t("sidebar.inventory", "Инвентар"), entries: inventoryEntries },
      ]
  ).filter((section) => section.entries.length > 0);

  if (sections.length === 0) return null;

  const activeSection = sections.find((section) => section.key === (activeSectionKey || sections[0]?.key)) || sections[0];

  return (
    <div
      className="relative"
      onMouseEnter={() => openMenu(menuKey)}
      onMouseLeave={closeMenuDelayed}
    >
      <button
        type="button"
        onClick={() => (open ? closeMenu() : openMenu(menuKey))}
        className="flex h-9 items-center gap-2 rounded-md px-3 text-sm font-semibold text-white hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-white/40"
      >
        <span>{menuLabel}</span>
        <ChevronDown size={15} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && activeSection && (
        <div
          className="absolute left-0 top-full mt-1 flex rounded-lg border border-gray-200 bg-white text-gray-800 shadow-2xl"
          onMouseEnter={cancelClose}
        >
          <div className="w-56 p-2">
            {sections.map((section) => {
              const active = section.key === activeSection.key;
              return (
                <button
                  key={section.key}
                  type="button"
                  onMouseEnter={() => setActiveSectionKey(section.key)}
                  onClick={() => setActiveSectionKey(section.key)}
                  className={`flex w-full items-center justify-between rounded px-3 py-2 text-left text-sm transition-colors ${
                    active ? "font-semibold" : "text-gray-700 hover:bg-gray-50"
                  }`}
                  style={active ? { color: "var(--color-primary)", backgroundColor: "color-mix(in srgb, var(--color-primary) 10%, white)" } : undefined}
                >
                  <span>{section.label}</span>
                  <ChevronRight size={15} />
                </button>
              );
            })}
          </div>

          <div className="max-h-[70vh] w-80 overflow-y-auto border-l border-gray-200 p-2">
            <div className="space-y-1">
              {activeSection.entries.map((entry) => {
                const active = entry.exact
                  ? location.pathname === entry.path
                  : location.pathname === entry.path || location.pathname.startsWith(`${entry.path}/`);
                return (
                  <React.Fragment key={`${activeSection.key}-${entry.key}`}>
                    {entry.divider && <div className="my-1 border-t border-gray-200" />}
                    <TopNavLink
                      to={entry.path}
                      state={entry.state}
                      label={entry.label}
                      moduleKey={entry.key}
                      isActive={active}
                      onClick={closeMenu}
                      className={`block truncate rounded px-2 py-1.5 text-xs transition-colors ${
                        active ? "font-semibold" : "text-gray-700 hover:bg-gray-50"
                      }`}
                      style={active ? { color: "var(--color-primary)", backgroundColor: "color-mix(in srgb, var(--color-primary) 10%, white)" } : undefined}
                    />
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TopStockManagementMenu;
